/**
 * pt_jobs — pressure testing. GET /dashboard/pt-jobs (api#1141): active and
 * scheduled jobs, active first. "Late" is the API's: still scheduled after its
 * day, in the tenant's zone. Dates are calendar days.
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { WidgetFigure, WidgetPanel } from '../WidgetPanel';
import { usePtJobs } from '../overviewQueries';
import { VerticalOff, OpsBadge, OpsRow, OpsWidgetRead, dash } from '../OpsWidgetRead';
import { ptJobStatusSkin } from '../../../lib/pressure-testing';
import { formatCalendarDate } from '../../../lib/calendar-date';
import type { PtJobs } from '../../../types/overview-operations';

function Body({ d }: { d: PtJobs }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <WidgetFigure testId="ptj-active" label="Active" value={d.counts.active} />
        <WidgetFigure testId="ptj-scheduled" label="Scheduled" value={d.counts.scheduled} />
        <WidgetFigure testId="ptj-today" label="Today" value={d.counts.today} />
        <WidgetFigure testId="ptj-late" label="Late" value={d.counts.late} alert={d.counts.late > 0} />
      </div>
      {d.jobs.length === 0 ? (
        <p data-testid="ptj-empty" className="mt-4 text-sm text-fieldiq-muted">No active or scheduled jobs.</p>
      ) : (
        <div data-testid="ptj-jobs" className="mt-4 divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
          {d.jobs.map((j) => {
            const skin = ptJobStatusSkin(j.status);
            return (
              <OpsRow key={j.id} to={j.link} onOpen={navigate} testId={`ptj-job-${j.id}`}>
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-fieldiq-text">{dash(j.job_number)} · {dash(j.location_name)}</span>
                  <span className="block truncate text-fieldiq-muted">{dash(j.customer_name)} · {dash(j.technician_name)}</span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <span className="flex gap-1">
                    <OpsBadge fill={skin.fill}>{skin.label}</OpsBadge>
                    {j.late && <OpsBadge fill="bg-fi-signal-red" testId="ptj-late-badge">Late</OpsBadge>}
                  </span>
                  <span className="tabular-nums text-fieldiq-text">{formatCalendarDate(j.scheduled_date, { month: 'short', day: 'numeric' }) ?? '—'}</span>
                </span>
              </OpsRow>
            );
          })}
        </div>
      )}
    </>
  );
}

export default function PtJobsWidget() {
  const { hasVertical } = useAuth();
  const on = hasVertical('pressure_testing');
  const q = usePtJobs(on);
  return (
    <WidgetPanel widgetKey="pt_jobs" eyebrow="Pressure testing" title="Test Jobs" link={on ? { label: 'Jobs', to: '/pressure-testing/jobs' } : undefined}>
      <div className="p-4">
        {!on ? (
          <VerticalOff testId="ptj-vertical-off">Pressure testing is not on for this company.</VerticalOff>
        ) : (
        <OpsWidgetRead q={q} subject="test jobs" allowed="admins, office staff, supervisors, dispatchers and logistics">
          {(d) => <Body d={d} />}
        </OpsWidgetRead>
        )}
      </div>
    </WidgetPanel>
  );
}
